const SELLER_TARGETED_TYPES = new Set<string>([
  "order_created",
  "cancellation_requested",
]);

// Compute the in-app destination for a notification given its type +
// dataJson payload. Returns null if there's nothing meaningful to
// route to (e.g. plain announcements, garden reminders without an
// associated entity, delivery offers that have their own action UI).
export function getNotificationPath(
  type: string,
  data: Record<string, any> | null | undefined,
): string | null {
  const d = data ?? {};

  if (type === "new_user_signup" && typeof d.newUserId === "string" && d.newUserId) {
    return `/u/${d.newUserId}`;
  }

  if (type === "referral_received" && typeof d.refereeId === "string" && d.refereeId) {
    return `/u/${d.refereeId}`;
  }

  if (type === "new_listing") {
    if (typeof d.itemId === "string" && d.itemId) return `/items/${d.itemId}`;
    if (typeof d.sellerId === "string" && d.sellerId) return `/u/${d.sellerId}`;
  }

  if (type === "triage_question" && typeof d.feedbackId === "string" && d.feedbackId) {
    return `/feedback/${d.feedbackId}`;
  }

  if (type === "first_listing_nudge") {
    return "/seller";
  }

  // ?stripe=start makes the seller dashboard open Stripe's hosted onboarding
  // on arrival. Without a branch here the tap fell through to /notifications.
  if (type === "payout_nudge") {
    return "/seller?stripe=start";
  }

  if (type === "support_message") {
    // Admins receive support DMs in the dedicated admin inbox — their own
    // /api/my/conversations excludes admin_support threads, so /messages
    // would land them on an empty page. Mirror the email CTA branch.
    // Deep-link to the sender's thread (?user=) so it opens directly —
    // AdminMessages injects + fetches that thread even when the
    // conversation-list query hasn't surfaced it yet, so the message is
    // always visible rather than buried in (or missing from) the list.
    if (d.recipientIsAdmin) {
      const senderId = typeof d.otherUserId === "string" ? d.otherUserId : "";
      return senderId
        ? `/admin/messages?user=${encodeURIComponent(senderId)}`
        : "/admin/messages";
    }
    return "/messages?support=1";
  }

  const orderId = typeof d.orderId === "string" ? d.orderId : "";
  if (SELLER_TARGETED_TYPES.has(type) && orderId) {
    return "/seller?tab=orders";
  }
  if (orderId) {
    return `/order-confirmation?orderId=${encodeURIComponent(orderId)}`;
  }

  return null;
}
